/**
 * Pure decisions for the Start view (task-8-brief.md Step 1), extracted so
 * they are pinned by exhaustive Node tests (test/startView.test.ts) instead
 * of a fourth DOM suite -- this plan's DOM-test scope is capped at three and
 * all three are already spoken for. Start.tsx calls these and renders the
 * answer; it decides nothing itself, the same split bootState.ts makes for
 * Boot.tsx and loginFlow.ts makes for Login.tsx.
 *
 * FIX ROUND 1 (review I-2) widened that split: `buildStartViewModel` is now
 * the ONE function that turns an `OverviewResponse` into everything the four
 * cards render, including which branch each card is in (never-connected /
 * connected / needs-reconnecting) -- decisions that used to live as JSX
 * ternaries in Start.tsx itself, where the review's four call-site
 * mutations all escaped untested. `Start.tsx` now reads this model and
 * switches on its discriminants; it computes nothing.
 *
 * `triggerRefreshCache`/`startRefreshCache` are the STANDING RULE piece
 * (controller, forward-carried from Task 7 review I-1): pinning a decision
 * is not enough, the ORCHESTRATION that applies it -- the thing a call-site
 * mutation could quietly break -- must also be Node-testable, collaborators
 * passed as parameters, the same shape as Boot.tsx's `performLogout` and
 * Login.tsx's `submitRequestCode`. The property this exists to protect:
 * "Cache aktualisieren" is `mode: 'inline'` (admin spec 4.4), so the
 * response body IS the job's final state -- a status-blind success path
 * here is "a job shown as finished when it failed", the controller's own
 * example of what this rule guards against.
 */
import { ApiError } from './api';
import { billingBanner } from './capabilities';
import { billingBannerText } from './billingCopy';
import { t } from './i18n';
import { errorMessage } from '../components/ErrorNotice';
import { invalidateMutation, reportAuthFailure } from '../queries';
import type { QueryClient } from '@tanstack/react-query';
import type {
  EbayConnection, EbayConnectionStatus, JobStatus, OverviewResponse, PublicJob, SheetSummary,
  TenantStatus, TenantSummary,
} from './types';
import type { PillKind } from '../components/StatusPill';

export function tenantStatusPill(status: TenantStatus): { kind: PillKind; label: string } {
  switch (status) {
    case 'trial': return { kind: 'neutral', label: t('status.tenant.trial') };
    case 'active': return { kind: 'ok', label: t('status.tenant.active') };
    case 'past_due': return { kind: 'warn', label: t('status.tenant.past_due') };
    case 'locked': return { kind: 'error', label: t('status.tenant.locked') };
    case 'canceled': return { kind: 'error', label: t('status.tenant.canceled') };
  }
}

/** `lastError` alone decides ok vs. warn -- `validatedAt` is displayed
 *  alongside it, not consulted for the kind, so a fresh binding that has
 *  simply never been validated yet ALSO reads as `ok` rather than `warn`. */
export function sheetPill(sheet: SheetSummary): { kind: PillKind; label: string } {
  return sheet.lastError
    ? { kind: 'warn', label: t('start.sheet.warn') }
    : { kind: 'ok', label: t('start.sheet.ok') };
}

export function ebayStatusPill(status: EbayConnectionStatus): { kind: PillKind; label: string } {
  switch (status) {
    case 'active': return { kind: 'ok', label: t('status.ebay.active') };
    case 'revoked': return { kind: 'warn', label: t('status.ebay.revoked') };
    case 'error': return { kind: 'error', label: t('status.ebay.error') };
  }
}

/**
 * OAuth scopes arrive space-delimited (RFC 6749 §3.3; adminRoutes.ts:1155
 * reads `tok.scope` straight off the provider's token response). Splitting
 * on ANY whitespace, not a single space, so a stray tab or double space in a
 * provider response does not produce an empty-string list entry -- the eBay
 * card is "where an owner would notice a connection they did not make"
 * (task-8-brief.md), and a blank bullet in that list is not that.
 */
export function splitScopes(scopes: string | null): string[] {
  if (!scopes) return [];
  return scopes.split(/\s+/).filter((s) => s.length > 0);
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));
}

export interface TenantCardModel {
  pill: { kind: PillKind; label: string };
  billingLine: string | null;
}

export function tenantCardModel(tenant: TenantSummary, nowMs: number): TenantCardModel {
  return {
    pill: tenantStatusPill(tenant.status),
    billingLine: billingBannerText(billingBanner(tenant, nowMs)),
  };
}

export type SheetCardModel =
  | { kind: 'never' }
  | {
      kind: 'connected';
      pill: { kind: PillKind; label: string };
      /** Already the full "Zuletzt geprüft: …" sentence, or `null` when the
       *  binding has never been validated -- computed HERE so Start.tsx has
       *  no fallback-date branch of its own to get wrong (review I-2,
       *  mutation C: a mutant defaulting this to `new Date(0)` must die in
       *  this file's own tests, not escape through an untested ternary). */
      validatedLine: string | null;
      serviceAccount: boolean;
      errorLine: string | null;
    };

export function sheetCardModel(sheet: SheetSummary | null): SheetCardModel {
  if (sheet === null) return { kind: 'never' };
  return {
    kind: 'connected',
    pill: sheetPill(sheet),
    validatedLine: sheet.validatedAt === null
      ? null
      : t('start.sheet.validatedAt', { date: formatDateTime(sheet.validatedAt) }),
    serviceAccount: sheet.connectedVia === 'service_account',
    errorLine: sheet.lastError,
  };
}

export type EbayCardModel =
  | { kind: 'never' }
  | { kind: 'active'; pill: { kind: PillKind; label: string }; connectedAtLine: string; scopes: string[] }
  | { kind: 'inactive'; pill: { kind: PillKind; label: string }; note: string };

/**
 * Review I-1: the server never deletes a `connections` row on disconnect,
 * it flips `status` to `'revoked'` and leaves `scopes`/`connected_at` as
 * they were (app-3a's db/tenantRepo.ts:156-166, adminRoutes.ts:1245) -- so
 * `connection !== null` alone is NOT "connected". Only `status === 'active'`
 * is. Every other value -- `'revoked'`, the declared-but-unwritten `'error'`,
 * or an unrecognised string the unchecked `adminFetch` cast could still hand
 * back -- is `'inactive'` and gets the SAME reconnect route a never-connected
 * tenant gets (Start.tsx renders one link for both `'never'` and
 * `'inactive'`), fail-closed the same way capabilities.ts's
 * EBAY_HEALTHY_STATUSES allowlist is: checking for the one good value,
 * never for the bad ones by name.
 */
export function ebayCardModel(connection: EbayConnection | null): EbayCardModel {
  if (connection === null) return { kind: 'never' };
  if (connection.status === 'active') {
    return {
      kind: 'active',
      pill: ebayStatusPill('active'),
      connectedAtLine: t('start.ebay.connectedAt', { date: formatDateTime(connection.connectedAt) }),
      scopes: splitScopes(connection.scopes),
    };
  }
  // Not flattened into one generic "inactive" label: 'error' still renders
  // as 'error' (something is broken), anything else -- including 'revoked'
  // and any value outside the 3-literal union -- reads as 'revoked' (this
  // tenant disconnected, deliberately or not). Two distinct pills, one
  // reconnect mechanism.
  const status: EbayConnectionStatus = connection.status === 'error' ? 'error' : 'revoked';
  return { kind: 'inactive', pill: ebayStatusPill(status), note: t('start.ebay.reconnectNeeded') };
}

export interface StartViewModel {
  tenant: TenantCardModel;
  sheet: SheetCardModel;
  ebay: EbayCardModel;
  jobs: PublicJob[];
}

/** The Start view's one query, turned into everything the four cards need.
 *  `jobs` is the one field passed through verbatim -- each row's own
 *  decisions (pill, "Dauer", relative time, truncation) already live in
 *  lib/jobDisplay.ts and are `<JobLine>`'s job, not this view's. */
export function buildStartViewModel(data: OverviewResponse, nowMs: number): StartViewModel {
  return {
    tenant: tenantCardModel(data.tenant, nowMs),
    sheet: sheetCardModel(data.sheet),
    ebay: ebayCardModel(data.ebay),
    jobs: data.jobs,
  };
}

/** `runAction`'s own response shape (adminRoutes.ts:1676-1685, inline mode). */
export interface RefreshCacheResponse {
  jobId: string;
  status: JobStatus;
  error?: string | null;
  stats?: unknown;
}

export type RefreshMessageKind = 'ok' | 'error';

export interface RefreshCacheIO {
  runAction: (action: string) => Promise<RefreshCacheResponse>;
  invalidate: () => void;
  /**
   * A 401 mid-action, driven into the session state (final review I-1).
   * Injected the same way `invalidate` is, and for the same reason: this
   * function is Node-callable with no QueryClient, and `startRefreshCache`
   * below wires the REAL `reportAuthFailure` with the REAL client so a test
   * can assert the `me` query was actually invalidated rather than that some
   * function was called.
   */
  reportAuthFailure: (error: unknown) => void;
  setMessage: (message: string) => void;
  setMessageKind: (kind: RefreshMessageKind) => void;
}

/**
 * Applies the three rules Step 1 states for "Cache aktualisieren":
 *
 *  1. It is `mode: 'inline'` -- the response IS the final status, so success
 *     is `result.status !== 'error'`, never "the promise resolved".
 *  2. `invalidate()` runs whenever a job row was actually created (any
 *     resolved response, success OR a failed job body) -- adminRoutes.ts
 *     always writes the row before responding, so the jobs list and the
 *     overview it feeds are stale either way. It does NOT run on a thrown
 *     ApiError (409/5xx/network): no row was created, nothing to refresh.
 *  3. `error.job_running` / `error.tenant_busy` render the exact copy Step 1
 *     names, using the 409's own payload for `tenant_busy` -- every other
 *     thrown error falls back to the shell's ordinary generic-error copy
 *     (components/ErrorNotice.tsx's `errorMessage`), the same fallback
 *     Login.tsx uses for its own non-anticipated failures.
 */
export async function triggerRefreshCache(io: RefreshCacheIO): Promise<void> {
  try {
    const result = await io.runAction('refresh-cache');
    io.invalidate();
    if (result.status === 'error') {
      io.setMessage(result.error
        ? t('start.actions.refreshCacheFailed', { error: result.error })
        : t('start.actions.refreshCacheFailedGeneric'));
      io.setMessageKind('error');
    } else {
      io.setMessage(t('start.actions.refreshCacheDone'));
      io.setMessageKind('ok');
    }
  } catch (error) {
    // FIRST, before the classification below: a 401 is the session, not this
    // action, and the shell above this message must stop claiming otherwise.
    io.reportAuthFailure(error);
    const code = error instanceof ApiError ? error.code : null;
    if (code === 'job_running') {
      io.setMessage(t('error.job_running'));
    } else if (code === 'tenant_busy') {
      const type = error instanceof ApiError && typeof error.body.runningType === 'string'
        ? error.body.runningType
        : '';
      io.setMessage(t('error.tenant_busy', { type }));
    } else {
      io.setMessage(errorMessage(error));
    }
    io.setMessageKind('error');
  }
}

/**
 * `INVALIDATES.runAction`, APPLIED. A separate, Node-testable function (real
 * QueryClient, no DOM) rather than an inline arrow at the call site, so a
 * mutation that hand-invalidates a DIFFERENT key -- or none -- reddens a test
 * without needing to render <Start>. Reads the shared map rather than
 * hardcoding queryKeys.jobs/queryKeys.overview so this stays correct if the
 * map ever changes (queries.ts's own docblock: "the map is where a reviewer
 * can see whether it does").
 */
export function invalidateRunAction(queryClient: QueryClient): void {
  // Delegates to queries.ts's shared `invalidateMutation` (Task 9): the
  // lookup-and-loop was duplicated the moment a second view needed it, and
  // the `?? []` no-op rationale now lives once, with the map.
  invalidateMutation(queryClient, 'runAction');
}

export interface StartRefreshCacheIO {
  queryClient: QueryClient;
  runAction: RefreshCacheIO['runAction'];
  setPending: (pending: boolean) => void;
  setMessage: (message: string) => void;
  setMessageKind: (kind: RefreshMessageKind) => void;
}

/**
 * Review I-2, mutation B: the button's `onClick` used to build the
 * `invalidate` collaborator inline in `Start.tsx` (`() =>
 * invalidateRunAction(queryClient)`), so nothing proved that WAS the
 * function actually wired in rather than a different key, or none. Moved
 * here so a Node test can call this with a REAL `QueryClient` (the same
 * pattern `performLogout`'s own test uses) and assert BOTH the message set
 * and the query cache's `isInvalidated` state afterward -- proving the real
 * collaborator, not a swapped-in one, ran. `setPending` is threaded through
 * (not left to the caller's own try/finally) so that guarantee -- pending
 * always ends false, success or failure -- is asserted here too rather than
 * trusted at the JSX call site.
 */
export async function startRefreshCache(io: StartRefreshCacheIO): Promise<void> {
  io.setPending(true);
  try {
    await triggerRefreshCache({
      runAction: io.runAction,
      invalidate: () => invalidateRunAction(io.queryClient),
      reportAuthFailure: (error) => reportAuthFailure(io.queryClient, error),
      setMessage: io.setMessage,
      setMessageKind: io.setMessageKind,
    });
  } finally {
    io.setPending(false);
  }
}
